import { useState, useEffect } from 'react';
import { Box, Heading, SimpleGrid, Card, CardBody, Text, Icon, VStack, useToast, Flex, useDisclosure, Badge, Tooltip, Collapse, HStack, useBreakpointValue, Tabs, TabList, Tab, TabPanels, TabPanel } from '@chakra-ui/react';
import { FiUsers, FiCheckCircle, FiGrid, FiSettings, FiUser, FiActivity, FiMic, FiUserCheck, FiCalendar, FiWifi, FiWifiOff, FiCoffee, FiChevronDown, FiChevronUp, FiEdit, FiFileText } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useWebSocket } from '../context/WebSocketContext';
import { useNavigate } from 'react-router-dom';
// import DashboardNotificationWidget from '../components/DashboardNotificationWidget'; // Removed per user request
// import DashboardLeavesWidget from '../components/DashboardLeavesWidget'; // Replaced
import DashboardLogisticsWidget from '../components/DashboardLogisticsWidget';
import DashboardCommBookWidget from '../components/DashboardCommBookWidget'; // Phase 28
import LeaveApplicationModal from '../components/LeaveApplicationModal';

// FeatureCard is defined below

const FeatureCard = ({ label, icon, color, path, onClickAction, isImplemented = true, h = "120px", iconSize = 6 }) => {
    const navigate = useNavigate();
    const toast = useToast();

    const handleClick = () => {
        if (!isImplemented) {
            toast({
                title: "功能建構中",
                description: "此功能尚未開放，敬請期待",
                status: "info",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        if (onClickAction) {
            onClickAction();
            return;
        }

        if (path) {
            navigate(path);
        }
    };

    return (
        <Card
            cursor="pointer"
            _hover={{ transform: 'translateY(-4px)', shadow: 'md' }}
            transition="all 0.2s"
            onClick={handleClick}
            h={h}
        >
            <CardBody display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={2}>
                <Icon as={icon} boxSize={iconSize} color={`${color}.500`} mb={2} opacity={isImplemented ? 1 : 0.5} />
                <Text fontWeight="bold" fontSize="sm" textAlign="center" lineHeight="1.2" color={isImplemented ? "gray.700" : "gray.400"}>
                    {label}
                </Text>
                {!isImplemented && (
                    <Text fontSize="xs" color="gray.400" mt={1}>
                        (未實作)
                    </Text>
                )}
            </CardBody>
        </Card>
    );
};

const CollapsibleSection = ({ title, children, badgeCount, defaultIsOpen = false, colorScheme = "gray" }) => {
    const isMobile = useBreakpointValue({ base: true, lg: false });
    const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: isMobile ? defaultIsOpen : true });

    if (!isMobile) {
        return (
            <Flex direction="column" h="100%" overflow="hidden">
                <HStack mb={3}>
                    <Heading size="md" color="gray.700" fontSize="lg">{title}</Heading>
                    {badgeCount > 0 && <Badge colorScheme="red" borderRadius="full" px={2}>{badgeCount}</Badge>}
                </HStack>
                {children}
            </Flex>
        );
    }

    return (
        <Box
            mb={{ base: 0, lg: 4 }}
            border="1px solid" borderColor="gray.200"
            borderRadius={{ base: "none", lg: "lg" }}
            borderTopRadius="lg"
            bg="white" overflow="hidden" shadow="xl"
            position={{ base: "fixed", lg: "relative" }}
            bottom={{ base: "60px", lg: "auto" }}
            left={{ base: "0", lg: "auto" }}
            right={{ base: "0", lg: "auto" }}
            width={{ base: "100%", lg: "auto" }}
            zIndex={{ base: 20, lg: 1 }}
        >
            <Flex
                p={3}
                justify="space-between"
                align="center"
                onClick={onToggle}
                bg="gray.50"
                cursor="pointer"
                _hover={{ bg: "gray.100" }}
                borderBottom={isOpen ? "1px solid" : "none"}
                borderColor="gray.200"
            >
                <HStack>
                    <Heading size="sm" color="gray.700">{title}</Heading>
                    {badgeCount > 0 && <Badge colorScheme="red" borderRadius="full" px={2}>{badgeCount}</Badge>}
                </HStack>
                <Icon as={isOpen ? FiChevronUp : FiChevronDown} color="gray.500" />
            </Flex>
            <Collapse in={isOpen} animateOpacity>
                <Box p={2} h={{ base: "600px", md: "100%" }} display="flex" flexDirection="column">
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
};

const Dashboard = () => {
    const { user } = useAuth();
    const { notifications } = useNotifications();
    const badgeCount = notifications.filter(n => !n.is_read).length; // Or total? Use User preference. I'll use total recent for consistency with old badge
    const toast = useToast();
    const { lastMessage, isConnected } = useWebSocket();

    // Auto-refresh trigger for widgets
    const [widgetRefreshKey, setWidgetRefreshKey] = useState(0);

    // WebSocket Listener for Global Updates
    // WebSocket Listener for Global Updates
    useEffect(() => {
        if (lastMessage && lastMessage.type === 'STUDENT_STATUS_UPDATE') {
            console.log("Dashboard received global update:", lastMessage);

            const actionText = lastMessage.action === 'LEAVE_CREATED' ? '新增請假' :
                lastMessage.action === 'LEAVE_DELETED' ? '取消請假' : '狀態更新';

            const leaveTypeMap = {
                'personal': '事假',
                'sick': '病假',
                'official': '公假',
                'other': '其他'
            };
            const typeLabel = leaveTypeMap[lastMessage.leave_type] || lastMessage.leave_type || '請假';
            const studentInfo = lastMessage.student_name ?
                `${lastMessage.class_name ? lastMessage.class_name + ' ' : ''}${lastMessage.student_name}` :
                `學生 #${lastMessage.student_id}`;

            // DEBUG: Show toast to confirm signal reception
            toast({
                title: actionText,
                description: `${studentInfo} - ${typeLabel}`,
                status: lastMessage.action === 'LEAVE_DELETED' ? "warning" : "info",
                duration: 4000,
                isClosable: true,
                position: "top-right"
            });
            setWidgetRefreshKey(prev => prev + 1);
        }
    }, [lastMessage]);

    // Leave Modal State
    const {
        isOpen: isLeaveModalOpen,
        onOpen: onOpenLeaveModal,
        onClose: onCloseLeaveModal
    } = useDisclosure();

    // 定義各角色的功能區塊
    // colors: blue, green, purple, orange, red, teal, cyan, pink

    // 機構管理員 (Admin / Super Admin)
    const adminFeatures = [
        { label: '每日點名狀況', icon: FiCheckCircle, color: 'blue', path: '/daily-attendance', isImplemented: true },
        { label: '接送控制台', icon: FiActivity, color: 'red', path: '/pickup-monitor', isImplemented: true },
        { label: '班級點名', icon: FiCheckCircle, color: 'orange', path: '/roll-call', isImplemented: true },
        { label: '學生管理', icon: FiUsers, color: 'green', path: '/students', isImplemented: true },
        { label: '班級管理', icon: FiGrid, color: 'teal', path: '/classes', isImplemented: true },
        { label: '請假管理', icon: FiCalendar, color: 'pink', path: '/leaves', isImplemented: true },
        { label: '教職員管理', icon: FiUserCheck, color: 'blue', path: '/staff', isImplemented: true },
        { label: '家長管理', icon: FiUser, color: 'cyan', path: '/parents', isImplemented: true },
        { label: '英文補課', icon: FiCalendar, color: 'purple', path: '/makeup', isImplemented: true },
        { label: '訂餐管理', icon: FiCoffee, color: 'orange', path: '/lunch', isImplemented: true },
        { label: '電子聯絡簿', icon: FiEdit, color: 'blue', path: '/teacher/comm-book', isImplemented: true },
        { label: '聯絡簿紀錄', icon: FiFileText, color: 'teal', path: '/teacher/history-comm-book', isImplemented: true },
        { label: '登記請假', icon: FiCalendar, color: 'gray', onClickAction: onOpenLeaveModal, isImplemented: true },
    ];

    // 行政人員 (Receptionist) - Same as Admin for now based on request
    const receptionistFeatures = [
        ...adminFeatures
    ];

    // 老師 (Teacher)
    const teacherFeatures = [
        { label: '班級點名', icon: FiCheckCircle, color: 'orange', path: '/roll-call', isImplemented: true },
        { label: '學生管理', icon: FiUsers, color: 'green', path: '/students', isImplemented: true },
        { label: '我的班級', icon: FiGrid, color: 'blue', path: '/my-class', isImplemented: true },
        { label: '英文補課', icon: FiCalendar, color: 'purple', path: '/makeup', isImplemented: true },
        { label: '訂餐管理', icon: FiCoffee, color: 'orange', path: '/lunch', isImplemented: true },
        { label: '電子聯絡簿', icon: FiEdit, color: 'blue', path: '/teacher/comm-book', isImplemented: true },
        { label: '聯絡簿紀錄', icon: FiFileText, color: 'teal', path: '/teacher/history-comm-book', isImplemented: true },
        { label: '登記請假', icon: FiCalendar, color: 'cyan', onClickAction: onOpenLeaveModal, isImplemented: true },
    ];

    // 根據角色決定顯示哪些功能
    let features = [];
    if (user?.role === 'super_admin' || user?.role === 'admin') {
        features = adminFeatures;
    } else if (user?.role === 'receptionist') {
        features = receptionistFeatures;
    } else if (user?.role === 'teacher') {
        features = teacherFeatures;
    }

    const isMobile = useBreakpointValue({ base: true, lg: false });

    const roleLabel = {
        'super_admin': '超級管理員',
        'admin': '機構管理員',
        'receptionist': '行政老師',
        'teacher': '老師'
    }[user?.role] || '使用者';

    const displayName = user?.full_name ? `${user.full_name} (${roleLabel})` : roleLabel;

    return (
        <Flex direction="column" h="100%" overflow="hidden">
            {/* Header Section - Fixed */}
            <Box mb={8} flexShrink={0} pt={2}>
                <Heading size={{ base: "xl", md: "lg" }} color="brand.900" mb={2} display="flex" alignItems="center" gap={2}>
                    早安，{displayName}
                    <Tooltip label={isConnected ? "即時連線正常" : "連線中斷"} placement="right">
                        <span>
                            {isConnected ? (
                                <Icon as={FiWifi} color="green.500" boxSize={6} />
                            ) : (
                                <Icon as={FiWifiOff} color="red.500" boxSize={6} />
                            )}
                        </span>
                    </Tooltip>
                </Heading>
                <Text color="neutral.500" fontSize={{ base: "md", md: "sm" }}>這裡是今日的接送概況與快速功能入口。</Text>
            </Box>

            {/* Content Section - 2 Column Layout (Optimized) */}
            <Box flex="1" overflow={{ base: "auto", lg: "hidden" }} pb={4} pr={1}>
                {/* Desktop: 2 Columns (2:1 Ratio), Mobile: 1 Column */}
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} h={{ base: "auto", lg: "100%" }} templateColumns={{ lg: "2fr 1fr" }}>

                    {/* Column 1: Feature Grid (Quick Actions) */}
                    <Box overflowY={{ base: "visible", lg: "auto" }} h={{ base: "auto", lg: "100%" }} pb={{ base: "100px", lg: 2 }} css={{
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                        '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '4px' },
                    }}>
                        <Heading size="md" mb={3} color="gray.700" fontSize="lg">快速功能</Heading>
                        <SimpleGrid columns={{ base: 2, sm: 3, lg: 2, xl: 3 }} spacing={3} pb={2}>
                            {features.map((feature, index) => (
                                <FeatureCard
                                    key={index}
                                    {...feature}
                                    h="90px" // Compact Height
                                    iconSize={6} // Compact Icon
                                />
                            ))}
                        </SimpleGrid>
                    </Box>

                    {/* Column 2: Operations (Status & Logistics) */}
                    <CollapsibleSection title="今日事項" defaultIsOpen={false}>
                        {/* Mobile + Teacher：tabs 切換聯絡簿 / 請假訂餐，避免聯絡簿把下方佔滿 */}
                        {isMobile && user?.role === 'teacher' ? (
                            <Tabs variant="soft-rounded" size="sm" h="100%" display="flex" flexDirection="column">
                                <TabList mb={2} px={1} flexShrink={0}>
                                    <Tab fontWeight="bold" color="gray.500" _selected={{ color: 'teal.600', bg: 'teal.50' }}>聯絡簿</Tab>
                                    <Tab fontWeight="bold" color="gray.500" _selected={{ color: 'blue.600', bg: 'blue.50' }}>請假 · 訂餐</Tab>
                                </TabList>
                                <TabPanels flex="1" overflow="hidden">
                                    <TabPanel p={0} h="100%">
                                        <DashboardCommBookWidget />
                                    </TabPanel>
                                    <TabPanel p={0} h="100%">
                                        <DashboardLogisticsWidget key={widgetRefreshKey} />
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        ) : (
                            /* 桌面版 or 非帶班老師：原本的上下堆疊 */
                            <Flex direction="column" gap={4} h="100%" overflow="hidden">
                                {user?.role === 'teacher' && (
                                    <Box flexShrink={0}>
                                        <DashboardCommBookWidget />
                                    </Box>
                                )}
                                <Box flex="1" overflow="hidden">
                                    <DashboardLogisticsWidget key={widgetRefreshKey} />
                                </Box>
                            </Flex>
                        )}
                    </CollapsibleSection>

                </SimpleGrid>
            </Box>
            {/* Modals */}
            <LeaveApplicationModal
                isOpen={isLeaveModalOpen}
                onClose={onCloseLeaveModal}
                onSuccess={() => {
                    // Refresh widget
                    setWidgetRefreshKey(prev => prev + 1);
                }}
            />
        </Flex>
    );
};

export default Dashboard;
